import { NailAppointmentConfigurationFragment } from '@client/graphql/nail-appointment-configuration-fragment';
import useUpdateAppointmentConfiguration from '@client/page/management/appointment/configuration/logic/use-update-appointment-configuration.ts';
import { useFocusedBranchContext } from '@client/page/management/table-group/table-group-list/component/branch-selection/focused-branch-context.ts';
import ToggleInput from '@packages/ui/form/input/toggle-input.tsx';
import { useDialog } from '@packages/ui/modal/use-dialog.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useTranslate } from '@tolgee/react';
import { getFragmentData } from 'core/src/graphql/types';
import { useEffect } from 'react';
import Loading from 'ui/src/loading';
import { useLocalStorage } from 'usehooks-ts';
import useAppointmentBranchConfiguration from '../logic/use-appointment-branch-configuration';

export default function AppointmentBranchConfigurationEnableInput() {
  const { t } = useTranslate();
  const branch = useFocusedBranchContext();
  const { data, loading, refetch } = useAppointmentBranchConfiguration(branch.branch?.id ?? '');
  const dialog = useDialog();

  const [update] = useUpdateAppointmentConfiguration();
  const { showAlert } = useNotificationCenter();
  const [enable, setEnable] = useLocalStorage<boolean>('appointmentEnable', false);

  const resolveData = getFragmentData(NailAppointmentConfigurationFragment, data?.appointmentBranchConfiguration);

  useEffect(() => {
    if (resolveData) {
      setEnable(!resolveData.enable);
    } else {
      setEnable(false);
    }
  }, [setEnable, resolveData]);

  if (loading) {
    return <Loading />;
  }

  if (!data) {
    return null;
  }

  if (!branch.branch) {
    return;
  }

  const onSubmit = async (value: boolean) => {
    return update({
      variables: {
        branchId: branch.branch?.id ?? '',
        input: {
          enable: !value,
        },
      },
    })
      .then(() => {
        setEnable(value);
        showAlert({
          message: t('appointment.configuration.update.success.header', 'Successfully'),
          status: 'success',
          title: t('appointment.configuration.update.success', 'Update appointment configuration successfully'),
        });
        void refetch();
      })
      .catch((e: Error) => {
        showAlert({
          message: t('appointment.configuration.update.error.header', 'Error'),
          status: 'error',
          title: e.message,
        });
      });
  };

  const showModal = (value: boolean) => {
    if (value) {
      dialog.confirmAlert({
        title: t('appointment.configuration.enable.confirm.title', 'Enable Appointment'),
        message: t(
          'appointment.configuration.enable.confirm.message',
          'Are you sure you want to enable appointment feature?',
        ),
        textButton: t('common.confirm', 'Confirm'),
        onConfirm: () => onSubmit(true),
      });
    }

    if (!value) {
      dialog.confirmAlert({
        title: t('appointment.configuration.disable.confirm.title', 'Disable Appointment'),
        message: t(
          'appointment.configuration.disable.confirm.message',
          'Are you sure you want to disable appointment feature?',
        ),
        textButton: t('common.confirm', 'Confirm'),
        onConfirm: () => onSubmit(false),
      });
    }
  };

  return (
    <div className="flex justify-center items-center gap-x-2 p-1.5 border border-gray-300 rounded-lg">
      <span className="font-semibold text-sm">{t('appointment.setting.enable.dashboard')}</span>
      <ToggleInput value={enable} onChange={showModal} />
    </div>
  );
}
